/**
 * The marks — SPEC §6.9, ported from `biandang_imagine/_assets/logos.js`.
 *
 * Same bitmaps, same 120×120 plate, same 0.36 dot and 0.70 halo, same sampling threshold, and the
 * colours **read off `:root` at runtime** so the mark cannot drift from the file that decided it.
 * That is also why there is no `tokens.ts`: one definition, in tokens.css, and both this component
 * and the size audit measure what actually shipped.
 *
 * Logo 1 is hand-set and needs no font, so it is right on the first paint. Logo 2's 便 and 當 cells
 * are sampled from the real Noto Sans TC glyphs and are redrawn once the face arrives, inside a
 * fixed viewBox, so **nothing on the page moves when it does**.
 *
 * **Logo 1 everywhere, including under 48px; logo 2 only at 64px and up** — its characters are nine
 * LEDs tall and turn to noise smaller. Sign-in and the wordmark lockup get logo 2.
 */

import { useEffect, useMemo, useState } from "react";

type Mark = "g" | "e" | "fav" | "board";

/** The five tokens that hold their values in both themes, read from `:root`. */
function boardColours() {
  const css = getComputedStyle(document.documentElement);
  const tok = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback;
  return {
    panel: tok("--panel", "#0A0B0C"),
    on: tok("--led", "#F2F2EE"),
    off: tok("--off", "#222427"),
    up: tok("--led-up", "#FF4D4D"),
    dn: tok("--led-dn", "#2FBF71"),
  };
}

const BM = {
  ARROW7: ["...#...", "..###..", ".#####.", "#######", "..###..", "..###..", "..###.."],
  BENTO8: [".######.", "........", "########", "#..#...#", "#..#.r.#", "####.r.#", "#..#...#", "########"],
  BENTO15: [
    "...............", ".#############.", ".#...........#.", ".#############.", "...............",
    ".#############.", ".#.....#.....#.", ".#.....#..r..#.", ".#.....#.rrr.#.", ".#######.rrr.#.",
    ".#.....#.rrr.#.", ".#.....#..r..#.", ".#.....#.....#.", ".#############.", "...............",
  ],
  A57: [".###.", "#...#", "#...#", "#####", "#...#", "#...#", "#...#"],
  I37: ["###", ".#.", ".#.", ".#.", ".#.", ".#.", "###"],
};

interface Grid {
  cols: number;
  rows: number;
  c: string[];
}

const bm = (rows: string[]): Grid => ({
  cols: rows[0].length,
  rows: rows.length,
  c: rows.join("").replace(/#/g, "a").split(""),
});

const blank = (cols: number, rows: number, ch = "."): Grid => ({
  cols,
  rows,
  c: new Array(cols * rows).fill(ch),
});

function put(dst: Grid, src: Grid, x: number, y: number, recolor?: string): Grid {
  for (let r = 0; r < src.rows; r += 1) {
    for (let q = 0; q < src.cols; q += 1) {
      const ch = src.c[r * src.cols + q];
      const X = x + q;
      const Y = y + r;
      if (ch === "." || X < 0 || Y < 0 || X >= dst.cols || Y >= dst.rows) continue;
      dst.c[Y * dst.cols + X] = recolor ?? ch;
    }
  }
  return dst;
}

/** 便 and 當, sampled from the real face at the same threshold brand-decisions.html used. */
function glyph(text: string, rows: number, cache: Map<string, Grid>): Grid {
  const key = `${text}|${rows}`;
  const held = cache.get(key);
  if (held) return held;

  const cell = 12;
  const H = rows * cell;
  const fs = H * 0.9;
  const weight = rows < 13 ? 900 : 700;
  const fam = "'Noto Sans TC',sans-serif";
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return blank(rows, rows);

  ctx.font = `${weight} ${fs}px ${fam}`;
  const cols = Math.ceil(ctx.measureText(text).width / cell) + 2;
  canvas.width = cols * cell;
  canvas.height = H;
  ctx.font = `${weight} ${fs}px ${fam}`;
  ctx.fillStyle = "#000";
  ctx.fillText(text, cell, H / 2 + fs * 0.37);

  const d = ctx.getImageData(0, 0, canvas.width, H).data;
  const on: boolean[] = [];
  const thr = rows < 13 ? 0.3 : 0.34;
  for (let r = 0; r < rows; r += 1) {
    for (let q = 0; q < cols; q += 1) {
      let a = 0;
      for (let yy = 0; yy < cell; yy += 1) {
        for (let xx = 0; xx < cell; xx += 1) {
          a += d[((r * cell + yy) * canvas.width + q * cell + xx) * 4 + 3];
        }
      }
      on.push(a / (cell * cell * 255) > thr);
    }
  }

  const emptyCol = (q: number) => {
    for (let r = 0; r < rows; r += 1) if (on[r * cols + q]) return false;
    return true;
  };
  let lo = 0;
  let hi = cols - 1;
  while (lo < hi && emptyCol(lo)) lo += 1;
  while (hi > lo && emptyCol(hi)) hi -= 1;

  const g = blank(hi - lo + 1, rows);
  for (let r = 0; r < rows; r += 1) {
    for (let q = lo; q <= hi; q += 1) {
      if (on[r * cols + q]) g.c[r * g.cols + q - lo] = "a";
    }
  }
  cache.set(key, g);
  return g;
}

const f2 = (n: number) => n.toFixed(2);

function dots(g: Grid, x0: number, y0: number, p: number, style: "glow" | "fav" | "flat", B: ReturnType<typeof boardColours>): string {
  let off = "";
  let halo = "";
  let lit = "";
  const r = p * 0.36;
  const sq = p * 0.94;
  for (let row = 0; row < g.rows; row += 1) {
    for (let q = 0; q < g.cols; q += 1) {
      const ch = g.c[row * g.cols + q];
      if (ch === " ") continue;
      const cx = x0 + q * p + p / 2;
      const cy = y0 + row * p + p / 2;
      const col = ch === "." ? B.off : ch === "r" ? B.up : ch === "g" ? B.dn : B.on;
      const el =
        style === "fav"
          ? `<rect x="${f2(cx - sq / 2)}" y="${f2(cy - sq / 2)}" width="${f2(sq)}" height="${f2(sq)}" fill="${col}"/>`
          : `<circle cx="${f2(cx)}" cy="${f2(cy)}" r="${f2(r)}" fill="${col}"/>`;
      if (ch === ".") {
        off += el;
      } else {
        lit += el;
        if (style === "glow") {
          halo += `<circle cx="${f2(cx)}" cy="${f2(cy)}" r="${f2(p * 0.7)}" fill="${col}" opacity=".2"/>`;
        }
      }
    }
  }
  return off + halo + lit;
}

const plate = (B: ReturnType<typeof boardColours>) =>
  `<rect x="1.5" y="1.5" width="117" height="117" rx="18" fill="${B.panel}" stroke="${B.off}" stroke-width="1.5"/>`;

const logoG = (B: ReturnType<typeof boardColours>) =>
  plate(B) + dots(bm(BM.BENTO15), 14, 14, 92 / 15, "glow", B);

function logoE(B: ReturnType<typeof boardColours>, cache: Map<string, Grid>): string {
  const centred = (ch: string) => {
    const g = glyph(ch, 9, cache);
    return put(blank(9, 9), g, Math.floor((9 - g.cols) / 2), 0);
  };
  const cells = [
    centred("便"),
    centred("當"),
    put(blank(9, 9), bm(BM.ARROW7), 1, 1, "r"),
    put(put(blank(9, 9), bm(BM.A57), 0, 1), bm(BM.I37), 6, 1),
  ];
  let s = `<rect x="1.5" y="1.5" width="117" height="117" rx="18" fill="${B.off}"/>`;
  ([[8, 8], [62, 8], [8, 62], [62, 62]] as const).forEach(([x, y], i) => {
    s +=
      `<rect x="${x}" y="${y}" width="50" height="50" rx="8" fill="${B.panel}"/>` +
      dots(cells[i], x + 3.5, y + 3.5, 43 / 9, "glow", B);
  });
  return s;
}

const favSvg = (B: ReturnType<typeof boardColours>, size: number) =>
  `<svg viewBox="0 0 16 16" width="${size}" height="${size}" aria-hidden="true">` +
  `<rect width="16" height="16" rx="3.5" fill="${B.panel}"/>` +
  `${dots(bm(BM.BENTO8), 1.5, 1.5, 13 / 8, "fav", B)}</svg>`;

function boardSvg(text: string, rows: number, B: ReturnType<typeof boardColours>, cache: Map<string, Grid>, pad = 1): string {
  const g = glyph(text, rows, cache);
  const grid = put(blank(g.cols + pad * 2, rows), g, pad, 0);
  const p = 10;
  return (
    `<svg viewBox="0 0 ${grid.cols * p} ${rows * p}" role="img" aria-label="${text}">` +
    `${dots(grid, 0, 0, p, "glow", B)}</svg>`
  );
}

interface Props {
  mark: Mark;
  /** Pixels. Logo 2 (`mark="e"`) is only drawn at 64 and up; below that it falls back to logo 1. */
  size?: number;
  label?: string;
  /** For `mark="board"`: the wordmark's text and how many LEDs tall it is. */
  text?: string;
  rows?: number;
}

export function Logo({ mark, size = 120, label = "便當AI", text = "便當AI", rows = 14 }: Props) {
  // The face arrives after the first paint, so logo 2 and the wordmark are drawn twice: once from
  // whatever is available, once from the real glyphs. The viewBox is fixed, so nothing moves.
  const [faceReady, setFaceReady] = useState(false);
  const theme = document.documentElement.dataset.theme ?? "";

  useEffect(() => {
    if (mark !== "e" && mark !== "board") return;
    if (!document.fonts) return;
    let alive = true;
    void Promise.race([
      Promise.all([
        document.fonts.load("900 40px 'Noto Sans TC'", "便當AI"),
        document.fonts.load("700 40px 'Noto Sans TC'", "便當AI"),
        document.fonts.ready,
      ]),
      new Promise((resolve) => window.setTimeout(resolve, 4_000)),
    ]).then(() => {
      if (alive) setFaceReady(true);
    });
    return () => {
      alive = false;
    };
  }, [mark]);

  const html = useMemo(() => {
    const B = boardColours();
    // A fresh cache per draw, because a re-draw after the face lands must not reuse the sampling
    // taken from the fallback face.
    const cache = new Map<string, Grid>();
    const wrap = (inner: string, px: number, name: string | null) =>
      `<svg viewBox="0 0 120 120" width="${px}" height="${px}" ${
        name ? `role="img" aria-label="${name}"` : 'aria-hidden="true"'
      }>${inner}</svg>`;

    if (mark === "fav") return favSvg(B, size === 120 ? 16 : size);
    if (mark === "board") return boardSvg(text, rows, B, cache);
    // Logo 2 only at 64px and up; smaller, its nine-LED characters turn to noise.
    if (mark === "e" && size >= 64) return wrap(logoE(B, cache), size, label);
    return wrap(logoG(B), size, label);
    // `theme` and `faceReady` are dependencies because both change what is drawn: the LED tokens
    // hold across themes, but the plate is read from :root all the same, and the glyphs change when
    // the real face lands.
  }, [mark, size, label, text, rows, faceReady, theme]);

  return <span className="brand__mark" dangerouslySetInnerHTML={{ __html: html }} />;
}
